/// <reference types="cordova-plugin-inappbrowser" />

import React from "react";

import { Box, Modal, ModalBody, ModalContent, ModalHeader, ModalOverlay, Text, } from "@chakra-ui/react";

import { useSettingsContext } from "../../contexts/SettingsContext";
import { HiOutlineArrowRight, HiOutlineExternalLink, HiOutlineLogout } from "react-icons/hi";
import APIconButton from "../APIconButton";
import APButton from "../APButton";
import { useDeviceInfo } from "../../contexts/DeviceContext";
import { Close } from "../APAssetAtlas";
import { IExerciserSDK } from "ap-exerciser-sdk";
import { useVocabIntl } from "../../hooks/useVocabTranslation";

interface SettingsModalProps {
  isLoggedIn: boolean,
  requestSignout: () => void,
  requestAccountDeletion: () => void,
  sdk: IExerciserSDK | undefined,
}

export default function SettingsModal({ isLoggedIn, requestSignout, requestAccountDeletion, sdk }: SettingsModalProps) {
  const { APFormattedMessage, getFormattedString } = useVocabIntl();
  const {
    settingModalOpen,
    setSettingModalOpen,
    setProfileOpen,
    setDefaultProfileTab,
  } = useSettingsContext();

  const handleOpenProfile = () => {
    setSettingModalOpen(false);
    setProfileOpen(true);
    setDefaultProfileTab(1);
  };

  const { inBrowser } = useDeviceInfo()

  const openPrivacyStatement = () => {
    cordova.InAppBrowser.open(process.env.REACT_APP_PRIVACY_URL!)
  }

  const openAccountParameters = () => {
    window.open(process.env.REACT_APP_ACCOUNT_SETTINGS_URL!)
  }

  return (
    <Modal
      closeOnOverlayClick={false}
      isOpen={settingModalOpen}
      onClose={() => setSettingModalOpen(false)}
      size={"sm"}>
      <ModalOverlay>
        <ModalContent>
          <ModalHeader>
            <Box sx={{}}>
              <Text
                textAlign={"center"}
                fontWeight={"700"}
                fontSize="1.125rem"
                lineHeight={1.5}
              >
                Paramètres
              </Text>
            </Box>
          </ModalHeader>
          <APIconButton variant={"raisedIcon"} rounded={"full"} tabIndex={2}
                        position={"absolute"} right={"-0.75rem"} top={"-0.75rem"} fontSize={"1.5rem"}
                        onClick={() => setSettingModalOpen(false)}
                        aria-label={getFormattedString('aria-label-close-modal')}>
            <Close />
          </APIconButton>
          <ModalBody padding={"1.5em"} display="flex" flexDir={"column"} alignItems={"stretch"} gap={"0.75rem"}>
            <Text
              fontWeight={"700"}
              fontSize="1rem"
              color="gray.550"
            >
              Personnalisation
            </Text>
            <APButton variant={"solid"} colorScheme={"tertiary"}
                      onClick={handleOpenProfile}
                      justifyContent={"space-between"}
                      size={"lg"}
                      tabIndex={1}
                      aria-label={getFormattedString('aria-label-change-wallpaper')}>
              <Text
                fontWeight={"700"}
                fontSize={["sm", "md"]}
                lineHeight={1.5}
              >
                <APFormattedMessage id={"change-the-wallpaper-button"} />
              </Text>
              <HiOutlineArrowRight />
            </APButton>
            {inBrowser && isLoggedIn && (
              <APButton flexFill fontSize={"lg"} variant={"solid"} colorScheme={"tertiary"}
                        justifyContent={"space-between"}
                        onClick={openAccountParameters}
                        tabIndex={2}
                        aria-label={getFormattedString('account-settings')}>
                <Text
                  fontWeight={"700"}
                  fontSize={["sm", "md"]}
                  lineHeight={1.5}
                >
                  <APFormattedMessage id={"account-settings"} />
                </Text>
                <HiOutlineExternalLink />
              </APButton>
            )}
            {!inBrowser &&
                <APButton flexFill fontSize={"lg"} variant={"solid"} colorScheme={"tertiary"}
                          justifyContent={"space-between"}
                          onClick={openPrivacyStatement}
                          tabIndex={2}
                          aria-label={getFormattedString('privacy-policy')}>
                  <Text
                      fontWeight={"700"}
                      fontSize={["sm", "md"]}
                      lineHeight={1.5}
                  >
                    <APFormattedMessage id={"privacy-policy"} />
                  </Text>
                  <HiOutlineExternalLink />
                </APButton>}
            {isLoggedIn && (
              <APButton flexFill fontSize={"lg"} variant={"solid"} colorScheme={"tertiary"}
                        justifyContent={"space-between"}
                        onClick={requestSignout}
                        tabIndex={2}
                        aria-label={getFormattedString('signout')}>
                <Text
                  fontWeight={"700"}
                  fontSize={["sm", "md"]}
                  lineHeight={1.5}
                >
                  <APFormattedMessage id={"signout"} />
                </Text>
                <HiOutlineLogout />
              </APButton>
            )}
            {isLoggedIn && (
              <APButton flexFill size={"sm"} variant={"solid"} colorScheme={"secondary"} textDecoration={"underline"}
                        color={"alert.fg"}
                        onClick={requestAccountDeletion}
                        tabIndex={2}
                        aria-label={getFormattedString('account-delete')}>
                <Text
                  fontWeight={"700"}
                  fontSize={["sm", "md"]}
                  lineHeight={1.5}
                >
                  <APFormattedMessage id={"account-delete"} />
                </Text>
              </APButton>
            )}
          </ModalBody>
        </ModalContent>
      </ModalOverlay>
    </Modal>
  );
}
